import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DprExportDataService, DprService } from '@dpr/services';
import {
  AccountType,
  Rights,
  SortDirection,
  StaticMessage,
  UtcDateTimeOptions,
} from '@shared/enums';
import { DateFilter, SelectedSubproject } from '@shared/models';
import { DprDetails } from '@shared/models/dpr';
import { DprAttachmentsInfoSync, DprCard, DprCardFilters } from '@dpr/models';
import {
  AppFacade,
  OfflineFacade,
  UserDetailsFacade,
} from '@shared/state/facades';
import { SubprojectConfigurationCustomNames } from '@shared/models/project-setup';
import { DataRefreshService, ToastNotificationService } from '@shared/services';
import {
  Constants,
  convertDateToJsonDateFormat,
  convertToDateWithoutTimeZoneSuffix,
  toUTCDate,
} from '@shared/utils';
import {
  DownloadDprModalComponent,
  SetupDprDateModalComponent,
} from '@dpr/components';
import { FileSaverService, FileService } from '@shared/services/files';
import { getStaticMessage } from '@shared/utils';
import { DprDownloadFilters } from '@dpr/models/dpr-download-filters.model';
import { PersistenceService } from '@shared/services/persistence';
import { ConfirmationDialogComponent } from '@grid/components';
import { ScreenSizeService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dprs-page',
  templateUrl: './dprs-page.component.html',
  styleUrls: ['./dprs-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprsPageComponent implements OnInit {
  isAppOnline$ = this.appFacade.query.isAppOnline$;
  isFullScreenMode$ = this.appFacade.query.isFullScreenMode$;
  approverList$ = this.userDetailsFacade.query.selectedSubproject$.pipe(
    map((selectedSubproject) => selectedSubproject?.data?.approverList)
  );
  onlineApproverList$ = this.userDetailsFacade.query.selectedSubproject$.pipe(
    map((selectedSubproject) => selectedSubproject?.data?.onlineApproverList)
  );

  selectedSubproject: SelectedSubproject;
  accountId: string;
  clientId: string;
  clientName: string;
  canEdit: boolean;
  enableCustomDates: boolean;
  isDigitalSignatureEnabled: boolean;
  canEditExternalPartyComment: boolean;
  customNames: SubprojectConfigurationCustomNames;
  defaultFilters: DprCardFilters;
  filters: DprCardFilters;
  accountType: AccountType;

  dprCardsCount$ = new BehaviorSubject<number>(null);
  dprCards$ = new BehaviorSubject<DprCard[]>([]);
  allowCreateDpr$ = new BehaviorSubject<boolean>(false);
  isLoadingBusy = false;
  isCreatingDpr = false;
  isDateRangeFilterOpen = false;
  isStatusFiltersOpen = false;
  hasFiltersApplied = false;
  sortByNewestToOldestTitle = $localize`:@@dpr.dprPages.txtNewestToOldest:Newest to Oldest`;
  sortByOldestToNewestTitle = $localize`:@@dpr.dprPages.txtOldestToNewest:Oldest to Newest`;
  statusFilterNames = {
    isCompleted: $localize`:@@dpr.dprPages.txtFilterStatusComplete:Complete`,
    isIncompleted: $localize`:@@dpr.dprPages.txtFilterStatusIncomplete:Incomplete`,
    isLocked: $localize`:@@dpr.dprPages.txtFilterStatusLocked:Locked`,
    isUnlocked: $localize`:@@dpr.dprPages.txtFilterStatusUnlocked:Unlocked`,
    isSignedDprDetailsUploaded: $localize`:@@dpr.dprPages.txtFilterSignedDprDetailsUploaded:Signed DPR Uploaded`,
    isSignedDprDetailsNotUploaded: $localize`:@@dpr.dprPages.txtFilterSignedDprDetailsNotUploaded:No Signed DPR Uploaded`,
  };
  sortingOptions = new Map<SortDirection, string>([
    [SortDirection.Descending, this.sortByNewestToOldestTitle],
    [SortDirection.Ascending, this.sortByOldestToNewestTitle],
  ]);
  entityName = getStaticMessage(StaticMessage.EntryLabel);
  entityNamePlural = getStaticMessage(StaticMessage.EntriesLabel);
  isAppOnline = true;
  isHandset: boolean;
  hasMoreResults = true;
  hasMoreThanOnePage = false;
  firstDprDate: Date | null;
  lastDprDate: Date | null;
  SortDirection = SortDirection;
  StaticMessage = StaticMessage;

  private mostRecentDprDateTime: Date;

  constructor(
    private dialog: MatDialog,
    private appFacade: AppFacade,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private dprService: DprService,
    private dataRefreshService: DataRefreshService,
    private dprExportDataService: DprExportDataService,
    private fileSaverService: FileSaverService,
    private fileService: FileService,
    private persistenceService: PersistenceService,
    private toastNotificationService: ToastNotificationService,
    protected screenSizeService: ScreenSizeService
  ) {}

  private get hasDprs(): boolean {
    return this.mostRecentDprDateTime != null;
  }

  ngOnInit(): void {
    this.userDetailsFacade.query.userDetails$
      .pipe(
        withLatestFrom(this.appFacade.query.dprsSortingDirection$),
        untilDestroyed(this),
        // this can happen on client or subproject change when the selected
        // subproject is not yet fully initialized
        // (the project setup is retrieved in a separate API call)
        filter(([userDetails]) => !!userDetails?.selectedSubproject?.data),
        mergeMap(([userDetails, sortDirection]) => {
          this.selectedSubproject = userDetails.selectedSubproject;
          this.accountType = userDetails.accountType;

          this.clientId = this.selectedSubproject.project.assignedClientId;
          this.clientName = userDetails.clientsList.find(
            (c) => c.id === this.clientId
          )?.name;

          const isAllowedToEditDprs =
            this.selectedSubproject.dprScreen === Rights.Edit &&
            !this.selectedSubproject.isLocked;
          this.canEdit =
            isAllowedToEditDprs &&
            this.selectedSubproject.data &&
            this.selectedSubproject.isReleased;

          const showExternalPartyComment =
            this.selectedSubproject.data?.subprojectConfiguration
              ?.showExternalPartyComment;
          this.canEditExternalPartyComment =
            !this.selectedSubproject.isLocked &&
            this.selectedSubproject.canEditExternalPartyComment &&
            showExternalPartyComment;

          this.enableCustomDates =
            this.selectedSubproject.data.subprojectConfiguration.enableCustomDates;
          this.accountId = userDetails.accountId;

          this.isDigitalSignatureEnabled =
            userDetails.clientsList?.find((x) => x.id === this.clientId)
              ?.enableDigitalSignature &&
            this.selectedSubproject.data?.subprojectConfiguration
              ?.enableDigitalSignature;

          this.customNames =
            this.selectedSubproject.data.subprojectConfiguration.customNames;

          this.defaultFilters = this.getDefaultFilters(sortDirection);
          this.refreshData();

          // Offline this reads a store selector that never completes, so only a
          // single snapshot is taken instead of holding a live subscription.
          return this.dprService.getLastThreeDprs().pipe(take(1));
        }),
        tap((lastThreeDprs) => {
          this.offlineFacade.setDprDetailsList(lastThreeDprs);
        })
      )
      .subscribe();

    this.dataRefreshService
      .getRefreshNeeded()
      .pipe(
        untilDestroyed(this),
        mergeMap(() => {
          // Close all open dialogs when data refresh is triggered
          // This handles cases where project setup or user rights change notifications
          // require page reload but modals remain open
          this.dialog.closeAll();

          this.refreshData();

          // we need to get and update last three DPRs when data refresh,
          // to avoid having outdated offline data in localStorage
          // Offline this reads a store selector that never completes, so only a
          // single snapshot is taken instead of holding a live subscription.
          return this.dprService.getLastThreeDprs().pipe(take(1));
        }),
        tap((lastThreeDprs) => {
          this.offlineFacade.setDprDetailsList(lastThreeDprs);
        })
      )
      .subscribe();

    // refresh the dpr cards when online state changes
    this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap((isAppOnline) => {
          this.isAppOnline = isAppOnline;
          this.refreshData();
        })
      )
      .subscribe();

    this.offlineFacade.query.mostRecentDprDetails$
      .pipe(
        untilDestroyed(this),
        tap((mostRecentDprDetails) => {
          // TODO(Improvement) check why on page load the date is a
          // Date object and after create it is a string
          this.mostRecentDprDateTime = mostRecentDprDetails
            ? toUTCDate(
                new Date(mostRecentDprDetails.dprDateTime),
                UtcDateTimeOptions.UtcDateWithoutTime
              )
            : null;
          this.filters.startingPoint = this.mostRecentDprDateTime;
          this.allowCreateDpr$.next(this.canEdit && this.canAddDpr());
        })
      )
      .subscribe();

    this.screenSizeService.isHandset$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap((isHandset) => {
          this.isHandset = isHandset;
          this.refreshData();
        })
      )
      .subscribe();
  }

  loadMoreCards(): void {
    this.getDprCards();
  }

  onSortColumnChanged(sortDirection: SortDirection): void {
    if (this.filters.sortingDirection === sortDirection) {
      return;
    }

    this.filters = { ...this.defaultFilters };
    this.filters.sortingDirection = sortDirection;
    this.appFacade.setDprsSortingDirection(sortDirection);

    this.onFiltersChanged();
  }

  onDateRangeFilterChanged(dateRange: {
    startDate: Date;
    endDate: Date;
  }): void {
    this.isDateRangeFilterOpen = false;
    this.filters.startDate = dateRange.startDate;
    this.filters.endDate = dateRange.endDate;

    this.onFiltersChanged();
  }

  onStatusFilterChanged(selection: Partial<DprCardFilters>): void {
    this.isStatusFiltersOpen = false;

    const newFilters = { ...this.filters, ...selection };
    if (JSON.stringify(this.filters) === JSON.stringify(newFilters)) {
      return;
    }

    this.filters = newFilters;

    this.onFiltersChanged();
  }

  onClearFilters(): void {
    this.filters = { ...this.defaultFilters };

    this.onFiltersChanged();
  }

  onDownloadExcel(): void {
    const toastId = this.toastNotificationService.info(
      $localize`:@@dpr.dprPages.txtDownloadExcel:Downloading excel`
    );

    this.dprExportDataService
      .exportData()
      .pipe(
        tap((response: HttpResponse<ArrayBuffer>) => {
          this.fileSaverService.saveFromBytes(response, toastId);
        })
      )
      .subscribe();
  }

  /**
   * @description After clicking the create new dpr button,
   * if no other DPRs exist or the Custom Dates feature is enabled, then
   * the user will be prompted to choose a start date,
   * which can be any date in the future.
   */
  onCreateDpr(): void {
    // The button keeps the focus after a click, so a held Enter key repeats the
    // activation and would fire concurrent creates that race the duplicate date check.
    if (this.isCreatingDpr) {
      return;
    }

    this.isCreatingDpr = true;

    //if custom date mode is disabled and there are DPR present, no need for date picker
    const createDpr$ =
      !this.enableCustomDates && this.hasDprs
        ? this.createDpr()
        : this.pickDprDate().pipe(switchMap((date) => this.createDpr(date)));

    createDpr$
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.isCreatingDpr = false;
        })
      )
      .subscribe();
  }

  onDownloadDprFiles(): void {
    const dialogRef = this.dialog.open(DownloadDprModalComponent, {
      ...Constants.DialogOptions,
      disableClose: false,
      data: {
        startDate: toUTCDate(
          this.firstDprDate,
          UtcDateTimeOptions.UtcDateWithoutTime
        ),
        endDate: toUTCDate(
          this.lastDprDate,
          UtcDateTimeOptions.UtcDateWithoutTime
        ),
      },
      panelClass: 'dialogCustom--largeSize',
      width: '750px',
    });

    let attachmentInfo: DprAttachmentsInfoSync = null;

    let toastId: number = null;

    dialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result) => !!result),
        switchMap((result: DprDownloadFilters) => {
          return this.dprService.getDprAttachmentsInfo(result);
        }),
        switchMap((response) => {
          if (response.status === 204) {
            this.dialog.open(ConfirmationDialogComponent, {
              ...Constants.DialogOptions,
              data: {
                title: $localize`:@@dpr.dprPages.txtNothingToDownload:Nothing to download`,
                message: $localize`:@@dpr.dprPages.txtNoDataFound:No data was found for the specified criteria`,
                hideCancelButton: true,
              },
            });

            return EMPTY;
          }

          toastId = this.toastNotificationService.info(
            $localize`:@@dpr.dprPages.txtDownloadDprFiles:Downloading DPR files`,
            true
          );

          attachmentInfo = response.body;
          return this.persistenceService.downloadZipArchive(
            response.body.fileDownloadInfo
          );
        }),
        takeWhile((x) => !!x),
        tap((result) => {
          this.fileService.download(
            result,
            attachmentInfo.fileName,
            attachmentInfo.fileType,
            toastId
          );
        })
      )
      .subscribe();
  }

  //#region Private methods

  private refreshData(): void {
    if (this.isLoadingBusy) {
      return;
    }

    this.filters = { ...this.defaultFilters };

    this.onFiltersChanged();
  }

  private getDefaultFilters(sortDirection?: SortDirection): DprCardFilters {
    const windowWidth = window?.innerWidth || null;
    let pageSize: number;

    switch (true) {
      case windowWidth > 2560: // > 2K monitors
        pageSize = 35;
        break;
      case windowWidth > 1920 && windowWidth <= 2560: // > Full HD && <= 2K monitors
        pageSize = 25;
        break;
      default:
        // <= Full HD monitors
        pageSize = 15;
    }

    return {
      pageNumber: 0,
      pageSize,
      startingPoint: null,
      sortingDirection: sortDirection ?? SortDirection.Descending,
      startDate: null,
      endDate: null,
      isCompleted: false,
      isIncompleted: false,
      isLocked: false,
      isUnlocked: false,
      isSignedDprDetailsNotUploaded: false,
      isSignedDprDetailsUploaded: false,
    };
  }

  /**
   * @description Gets the dprs based on selected
   * filters by increasing page number
   */
  private getDprCards(): void {
    if (
      this.isLoadingBusy ||
      !this.hasMoreResults ||
      !this.selectedSubproject
    ) {
      return;
    }

    this.isLoadingBusy = true;
    this.filters.pageNumber += 1;

    const httpParams = new HttpParams({
      fromObject: {
        ...this.filters,
        startDate: this.filters?.startDate
          ? convertToDateWithoutTimeZoneSuffix(this.filters.startDate)
          : null,
        endDate: this.filters?.endDate
          ? convertToDateWithoutTimeZoneSuffix(this.filters.endDate)
          : null,
        startingPoint: this.filters?.startingPoint
          ? convertToDateWithoutTimeZoneSuffix(this.filters.startingPoint)
          : null,
      },
    });

    this.dprService
      .getDprCards(httpParams)
      .pipe(
        untilDestroyed(this),
        tap((data) => {
          this.dprCardsCount$.next(data.totalCount);
          this.dprCards$.next([...this.dprCards$.value, ...data.dprCards]);
          this.hasMoreResults =
            this.dprCardsCount$.value > this.dprCards$.value.length;
          this.hasMoreThanOnePage = this.filters.pageNumber > 1;

          if (this.filters.pageNumber === 1) {
            this.filters.startingPoint = this.mostRecentDprDateTime;
            this.allowCreateDpr$.next(this.canEdit && this.canAddDpr());
          }

          this.lastDprDate = data.lastDprDate
            ? new Date(data.lastDprDate)
            : null;
          this.firstDprDate = data.firstDprDate
            ? new Date(data.firstDprDate)
            : null;
        }),
        map((data) => data.dprCards),
        catchError(() => {
          // INFO(CR): In case you receive 401 on the first DPRs call(triggered from init), then the Unauthorized modal will be shown,
          // but it will not be visible on the screen because another call to this method will be triggered by the infinite scroll library,
          // so the first Unauthorized modal will be covered by the second loading screen and so on...
          // This is the same bug for which we receive a lot of Unauthorized errors on email
          // Somehow the infinite scroll is triggered as long as there are no dprs shown (not deeply investigated, more of an assumption)
          this.hasMoreResults = false;
          return EMPTY;
        }),
        finalize(() => {
          this.isLoadingBusy = false;
        })
      )
      .subscribe();
  }

  private canAddDpr(): boolean {
    if (!this.mostRecentDprDateTime || this.enableCustomDates) {
      return true;
    }

    const projectSetupBlockFutureDates =
      !!this.selectedSubproject.data.subprojectConfiguration?.blockFutureDates;
    if (!projectSetupBlockFutureDates) {
      return true;
    }

    const currentDprDate = convertDateToJsonDateFormat(
      toUTCDate(new Date(), UtcDateTimeOptions.UtcDateWithoutTimeZone)
    );
    const lastDprDate = convertDateToJsonDateFormat(
      toUTCDate(
        new Date(this.mostRecentDprDateTime),
        UtcDateTimeOptions.UtcDateWithoutTimeZone
      )
    );
    return lastDprDate <= currentDprDate;
  }

  private pickDprDate(): Observable<Date> {
    const dialogRefObs = this.isAppOnline
      ? this.dprService
          .getAvailableDates()
          .pipe(
            switchMap((availableDates) =>
              this.openDateDialogObs(availableDates)
            )
          )
      : this.openDateDialogObs();

    return dialogRefObs.pipe(
      switchMap((dialogRef) => dialogRef.afterClosed()),
      // A cancelled dialog emits undefined and completes, which resets the guard.
      filter((date) => !!date)
    );
  }

  private openDateDialogObs(
    existingDprDates: DateFilter = null
  ): Observable<MatDialogRef<SetupDprDateModalComponent>> {
    return of(
      this.dialog.open(SetupDprDateModalComponent, {
        ...Constants.DialogOptions,
        disableClose: false,
        data: {
          lastDprDate: this.mostRecentDprDateTime,
          dprCards: this.dprCards$.value,
          existingDprDates,
          isAppOnline: this.isAppOnline,
          isSystemAdmin: this.accountType === AccountType.SystemAdmin,
        },
        ...(this.isHandset
          ? {
              panelClass: 'dialogCustom--mobileFullWidthScreen',
              position: { bottom: '0', left: '0', right: '0' },
            }
          : { width: '495px' }),
      })
    );
  }

  /**
   * @description Creates a new DPR and if it succeeds then it will
   *  - refresh the DPRs (by keeping the actual filters) to first page in order to include the last DPR (if it's not excluded by filters)
   *  - gets the last 3 DPRs
   */
  private createDpr(date?: Date): Observable<DprDetails[]> {
    return this.dprService.createDpr(this.selectedSubproject.id, date).pipe(
      mergeMap(() => {
        this.appFacade.setDprsSortingDirection(SortDirection.Descending);
        setTimeout(() => {
          this.defaultFilters = this.getDefaultFilters();
          this.refreshData();
        });
        // Offline this reads a store selector that never completes, so take a
        // single snapshot to let the stream terminate and release the guard.
        return this.dprService.getLastThreeDprs().pipe(take(1));
      }),
      tap((lastThreeDprs) => {
        this.offlineFacade.setDprDetailsList(lastThreeDprs);
      }),
      catchError(() => EMPTY)
    );
  }

  private onFiltersChanged(): void {
    this.filters.pageNumber = 0;
    this.filters.startingPoint = null;
    this.hasMoreResults = true;
    this.dprCards$.next([]);

    this.hasFiltersApplied =
      this.filters?.isCompleted ||
      this.filters?.isIncompleted ||
      this.filters?.isLocked ||
      this.filters?.isUnlocked ||
      this.filters?.isSignedDprDetailsUploaded ||
      this.filters?.isSignedDprDetailsNotUploaded ||
      !!this.filters?.startDate ||
      !!this.filters?.endDate;

    this.getDprCards();
  }
  //#endregion
}
